import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, DownloadOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../config/api';

interface WaitlistEntry {
  _id: string;
  email: string;
  name?: string;
  wellnessInterest?: string;
  consent: boolean;
  sourcePage?: string;
  joinedAt: string;
}

interface SurveyEntry {
  _id: string;
  userId?: { email?: string; username?: string };
  answers: { q1?: string; q2?: string; q3?: string; q4?: string; q5?: string };
  scanCount?: number;
  skipped: boolean;
  completedAt: string;
}

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [waitlistLoading, setWaitlistLoading] = useState(true);
  const [waitlistError, setWaitlistError] = useState('');
  const [waitlistSearch, setWaitlistSearch] = useState('');

  const [surveys, setSurveys] = useState<SurveyEntry[]>([]);
  const [surveysLoading, setSurveysLoading] = useState(true);
  const [surveysError, setSurveysError] = useState('');
  const [surveyFilter, setSurveyFilter] = useState<'all' | 'completed' | 'skipped'>('all');

  useEffect(() => {
    apiFetch('/api/waitlist')
      .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(d => setWaitlist(d.entries ?? []))
      .catch(e => setWaitlistError(`Failed to load waitlist (${e.message}).`))
      .finally(() => setWaitlistLoading(false));

    apiFetch('/api/surveys')
      .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(d => setSurveys(d.surveys ?? []))
      .catch(e => setSurveysError(`Failed to load surveys (${e.message}).`))
      .finally(() => setSurveysLoading(false));
  }, []);

  const filteredWaitlist = waitlist.filter(e =>
    !waitlistSearch || e.email.toLowerCase().includes(waitlistSearch.toLowerCase()) ||
    (e.name ?? '').toLowerCase().includes(waitlistSearch.toLowerCase())
  );

  const filteredSurveys = surveys.filter(s =>
    surveyFilter === 'all' ? true : surveyFilter === 'skipped' ? s.skipped : !s.skipped
  );

  const completedCount = surveys.filter(s => !s.skipped).length;
  const skipRate = surveys.length ? Math.round((surveys.filter(s => s.skipped).length / surveys.length) * 100) : 0;

  const exportWaitlistCSV = () => {
    apiFetch('/api/waitlist?format=csv').then(async r => {
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'waitlist.csv'; a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #0a1628 0%, #0d1f3c 60%, #0a1628 100%)',
      color: 'white', px: { xs: 2, md: 4 }, py: 3,
    }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>

        <Button startIcon={<ArrowBack sx={{ fontSize: 16 }} />} onClick={() => navigate('/')}
          sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontSize: '0.82rem', mb: 3, px: 0 }}>
          Back
        </Button>

        <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, mb: 0.5 }}>Admin</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.88rem', mb: 3 }}>
          Waitlist and survey responses
        </Typography>

        {/* Stats */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
          <StatChip label="Waitlist signups" value={waitlist.length} color="#63b3ed" />
          <StatChip label="Surveys received" value={surveys.length} color="#4ade80" />
          <StatChip label="Completed surveys" value={completedCount} color="#a78bfa" />
          <StatChip label="Skip rate" value={`${skipRate}%`} color="#fbbf24" />
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ mb: 3, '& .MuiTab-root': { color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontWeight: 700 }, '& .Mui-selected': { color: 'white' }, '& .MuiTabs-indicator': { bgcolor: '#63b3ed' } }}>
          <Tab label={`Waitlist (${waitlist.length})`} />
          <Tab label={`Surveys (${surveys.length})`} />
        </Tabs>

        {/* ── Waitlist tab ── */}
        {tab === 0 && (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search by email or name…"
                size="small"
                value={waitlistSearch}
                onChange={e => setWaitlistSearch(e.target.value)}
                sx={{ flex: 1, minWidth: 220, '& .MuiOutlinedInput-root': { color: 'white', borderRadius: '10px', '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' } }, '& input::placeholder': { color: 'rgba(255,255,255,0.3)' } }}
              />
              <Button variant="outlined" startIcon={<DownloadOutlined />} onClick={exportWaitlistCSV}
                sx={{ borderRadius: '10px', color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.2)', textTransform: 'none', fontWeight: 700, '&:hover': { borderColor: 'rgba(255,255,255,0.4)' } }}>
                Export CSV
              </Button>
            </Box>

            {waitlistError && <Alert severity="error" sx={{ mb: 2 }}>{waitlistError}</Alert>}
            {waitlistLoading ? <CircularProgress sx={{ color: '#63b3ed' }} /> : (
              <Box sx={tableSx}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Email', 'Name', 'Interest', 'Joined'].map(h => (
                        <TableCell key={h} sx={thSx}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredWaitlist.length === 0 ? (
                      <TableRow><TableCell colSpan={4} sx={{ ...tdSx, textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>No entries yet</TableCell></TableRow>
                    ) : filteredWaitlist.map(e => (
                      <TableRow key={e._id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                        <TableCell sx={tdSx}>{e.email}</TableCell>
                        <TableCell sx={tdSx}>{e.name ?? '—'}</TableCell>
                        <TableCell sx={tdSx}>{e.wellnessInterest ?? '—'}</TableCell>
                        <TableCell sx={tdSx}>{new Date(e.joinedAt).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}

        {/* ── Surveys tab ── */}
        {tab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {(['all', 'completed', 'skipped'] as const).map(f => (
                <Chip key={f} label={f.charAt(0).toUpperCase() + f.slice(1)} onClick={() => setSurveyFilter(f)}
                  sx={{ textTransform: 'capitalize', fontWeight: 700, cursor: 'pointer',
                    bgcolor: surveyFilter === f ? '#63b3ed' : 'rgba(255,255,255,0.08)',
                    color: surveyFilter === f ? '#0a1628' : 'rgba(255,255,255,0.6)',
                    '&:hover': { bgcolor: surveyFilter === f ? '#90cdf4' : 'rgba(255,255,255,0.14)' } }} />
              ))}
            </Box>

            {surveysError && <Alert severity="error" sx={{ mb: 2 }}>{surveysError}</Alert>}
            {surveysLoading ? <CircularProgress sx={{ color: '#63b3ed' }} /> : (
              <Box sx={tableSx}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['User', 'Scan #', 'Q1 Ease', 'Q2 Clarity', 'Q3 Useful', 'Q4 Feedback', 'Q5 Again', 'Date'].map(h => (
                        <TableCell key={h} sx={thSx}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSurveys.length === 0 ? (
                      <TableRow><TableCell colSpan={8} sx={{ ...tdSx, textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>No surveys yet</TableCell></TableRow>
                    ) : filteredSurveys.map(s => (
                      <TableRow key={s._id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                        <TableCell sx={tdSx}>{s.userId?.email ?? s.userId?.username ?? '—'}</TableCell>
                        <TableCell sx={tdSx}>{s.scanCount ?? '—'}</TableCell>
                        {s.skipped ? (
                          <TableCell colSpan={5} sx={{ ...tdSx, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>Skipped</TableCell>
                        ) : (
                          <>
                            <TableCell sx={tdSx}>{s.answers.q1 ?? '—'}</TableCell>
                            <TableCell sx={tdSx}>{s.answers.q2 ?? '—'}</TableCell>
                            <TableCell sx={tdSx}>{s.answers.q3 ?? '—'}</TableCell>
                            <TableCell sx={{ ...tdSx, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.answers.q4}>{s.answers.q4 || '—'}</TableCell>
                            <TableCell sx={tdSx}>{s.answers.q5 ?? '—'}</TableCell>
                          </>
                        )}
                        <TableCell sx={tdSx}>{new Date(s.completedAt).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const StatChip: React.FC<{ label: string; value: string | number; color: string }> = ({ label, value, color }) => (
  <Box sx={{ px: 2, py: 1, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>{label}</Typography>
    <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color }}>{value}</Typography>
  </Box>
);

const tableSx = {
  borderRadius: '14px',
  border: '1px solid rgba(255,255,255,0.08)',
  overflow: 'hidden',
  overflowX: 'auto' as const,
};

const thSx = {
  color: 'rgba(255,255,255,0.4)',
  fontSize: '0.72rem',
  fontWeight: 800,
  textTransform: 'uppercase' as const,
  letterSpacing: '1px',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
  bgcolor: 'rgba(255,255,255,0.03)',
  whiteSpace: 'nowrap' as const,
};

const tdSx = {
  color: 'rgba(255,255,255,0.75)',
  fontSize: '0.84rem',
  borderBottom: '1px solid rgba(255,255,255,0.05)',
};

export default AdminPage;
